import React, { useState, useEffect, useRef, useContext } from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    LoadingSpinner, Button, mapPlaylistFromVideoSearch, styles, dateTranslate, AUTO_PLAYLIST_TYPE, Dropdown, browser,
} from 'yankees-react-utils';
import usePlayerSelector from '../../../utils/usePlayerSelector';
import VideoPlayerAdvanced from '../../../components/VideoPlayerAdvanced';
import { PlayerReport, Playlists, Video, useOktaQuery } from '../../../data';
import VideoPlayer, { VideoPlayerModal } from '../../../components/VideoPlayer';
import MatchupPitcherSelector from './MatchupPitcherSelector';
import { store } from '../../../store';

const StyledSearchTitle = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: .5rem;
    h3 {
        margin-bottom: 0;
        > span {
            color: ${styles.theme.fontColors.doveGray};
            margin: 0 10px;
        }
    }
`;
const StyledReportHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: .5rem;
    > h3 {
        margin-bottom: 0;
    }
    > div {
        display: flex;
        align-items: center;
    }
`;

const MAIN_HD = 'Main HD';
const getPlaylist = (translate, clips, playlistName) => {
    const sequences = clips.reduce((allClips, clip) => {
        (allClips[clip.Sequence] = allClips[clip.Sequence] || []).push(clip);
        return allClips;
    }, {});
    return Object.entries(sequences).map(([, sequenceClips], videoIndex) => {
        const data = sequenceClips[0].Data ? JSON.parse(sequenceClips[0].Data) : null;
        const hasMainHDAngle = sequenceClips.some(({ AngleLabel }) => AngleLabel === MAIN_HD);
        return {
            title: data ? `${videoIndex + 1}. ${data.Batter}${data.Pitcher ? ` ${translate('baseball.abbr-versus')} ${data.Pitcher} - ${dateTranslate(translate, sequenceClips[0].GameDate)}` : ''}` : playlistName,
            description: data ? `${data.Inning ? `${translate('baseball.inning')}: ${data.Inning}` : ''}${data.Outs ? ` | ${translate('baseball.outs_one')}: ${data.Outs}` : ''} 
            ${data.Count ? ` | ${translate('baseball.abbr-count')}: ${data.Count}` : ''}${data.PitchType ? ` | ${data.PitchType}` : ''}
            ${data.Velocity ? ` (${data.Velocity})` : ''}${data.Result ? ` | ${translate('common.result')}: ${data.Result}` : ''}` : '',
            sources: sequenceClips.map((clip, clipIndex) => ({
                default: (hasMainHDAngle ? clip.AngleLabel === MAIN_HD : !clipIndex),
                file: clip.Url,
                label: clip.AngleLabel,
            })),
            clip: sequenceClips.map((clip) => ({
                ...clip,
                Include: true,
                Data: JSON.parse(clip.Data),
            })),
            data: {
                gameid: sequenceClips[0]?.GameID,
                eventseq: sequenceClips[0]?.EventSeq,
                pitchseq: sequenceClips[0]?.PitchSeq,
            },
            pitchId: (sequenceClips[0]?.GameID && sequenceClips[0]?.EventSeq && sequenceClips[0]?.PitchSeq) ? `${sequenceClips[0].GameID}.${sequenceClips[0].EventSeq}.${sequenceClips[0].PitchSeq}` : null,
        };
    });
};

const PlayerVideoReviewReport = ({ player, isPlayerLoading }) => {
    const { state: { user } } = useContext(store);
    const { t: translate } = useTranslation();
    const { PlayerID, IsCatcher, IsPitcher, Throws, Bats, TeamID, FullName } = usePlayerSelector();
    // The player passed in here could be the batter or the pitcher (depending on who is logged in)
    // So we need to check if it's a pitcher before decising what to do with various flags
    // eslint-disable-next-line no-nested-ternary
    const isLHB = IsPitcher ? (player?.Bats === 3 ? Throws === 1 : player?.Bats === 2)
        : (Bats === 3 ? player?.Throws === 1 : Bats === 2);
    const opposingPlayerId = player && player.PlayerID;
    const { batterId, pitcherId } = useParams();
    const isCatcherViewingPitcher = IsCatcher && batterId;
    const [pitcherIfCatcher, setPitcherIfCatcher] = useState();
    const pitcherNameUse = pitcherIfCatcher?.FullName || FullName;
    const pitcherIdUse = pitcherIfCatcher?.PlayerID || PlayerID;
    // eslint-disable-next-line no-nested-ternary
    const isLHP = pitcherIfCatcher ? pitcherIfCatcher.Throws === 2 : (IsPitcher ? (Throws === 2) : (player?.Throws === 2));
    const [activeSearchOption, setActiveSearchOption] = useState();
    const [playlistDataRaw, setPlaylistDataRaw] = useState();
    const [videoSearchOptions, setVideoSearchOptions] = useState([]);
    const [videosDisplay, setVideosDisplay] = useState(null);
    const [videosDisplaySimple, setVideosDisplaySimple] = useState(null);
    const { isFetching: isLoadingPlayerPlaylist } = useOktaQuery(['playlist-opposing-player', PlayerID, opposingPlayerId], (t) => Playlists.getOppsoingPlayerPlaylist(t, PlayerID, opposingPlayerId), { onSuccess: (data) => { setPlaylistDataRaw(data); processPlayerPlaylist(data); }, enabled: !!opposingPlayerId });
    const videoPlayerModalContainer = useRef();
    const [showVideoPlayerModal, setShowVideoPlayerModal] = useState(false);
    const [initialPageLoad, setInitialPageLoad] = useState(true);
    const [lastXGames, setLastXGames] = useState([]);

    const getLastAppearanceTitle = (g) => `${dateTranslate(translate, g.gamedate)}${g.dhtype > 1 ? ` - ${translate('baseball.abbr-game')}#${g.gamenum}` : ''}`;

    // Function the player's playlist data and determine which auto-generated playlists we need
    // and which ones we have overrides for in the API call
    function processPlayerPlaylist(data) {
        const videoSearchOptionsToSet = [];

        // Need to also account for the case of playlists that aren't in a category, like unrelated to the auto-gen or override lists
        // They should just pass right through as available playlists to the player.
        const allAutoPlaylistTypes = AUTO_PLAYLIST_TYPE.map((x) => x.id);
        const visibleInPortalNotAuto = data.filter((x) => (x.PlayListCategory === null || !allAutoPlaylistTypes.includes(x.PlayListCategory.AutoPlayListType))
            && x.PlayListCategory.IsVideoReviewButton === true);
        const visibleInPortal = data.filter((x) => x.PlayListCategory.IsVideoReviewButton === true);
        AUTO_PLAYLIST_TYPE.filter((x) => x.id > 0).forEach((playlistcategory) => {
            const matchingPlaylists = visibleInPortal.filter((x) => x.PlayListCategory?.AutoPlayListType === playlistcategory.id);
            if (matchingPlaylists?.length > 0) {
                // If the playlist exists, we use it over any auto-generated one
                videoSearchOptionsToSet.push(
                    { name: playlistcategory.name, playlists: matchingPlaylists },
                );
            } else {
                // Otherwise we'd need to dynamically grab it
                if (playlistcategory.id === 2 || playlistcategory.id === 7) {
                    // In the case of Pitch Recognition (ID2) and Pitcher Pickoffs (7) we don't create an auto-playlist
                    return;
                }
                if (playlistcategory.id === 1) {
                    // Pitch Mix
                    if (IsPitcher || isCatcherViewingPitcher) {
                        // Requires both BatterID and PitcherID
                        videoSearchOptionsToSet.push(
                            { name: playlistcategory.name, playlists: null, pitcherId: pitcherIdUse, batterId, pitchMix: true },
                        );
                    } else {
                        // Requires both PitcherID AND ISLHB (take note to account for switch hitters)
                        videoSearchOptionsToSet.push(
                            { name: playlistcategory.name, playlists: null, pitcherId, islhp: isLHP, islhb: isLHB, pitchMix: true },
                        );
                    }
                    return;
                }
                if (playlistcategory.id === 3 && !IsPitcher && !isCatcherViewingPitcher) {
                    // Damage, only for hitters
                    videoSearchOptionsToSet.push(
                        { name: playlistcategory.name, playlists: null, islhp: isLHP, pitcherId, islhb: isLHB, hardContact: true },
                    );
                    return;
                }
                if (playlistcategory.id === 4) {
                    // Recent Matchup (batter and pitcher)
                    // Pitcher (or catcher) vs hitter
                    if (batterId && PlayerID && (IsPitcher || IsCatcher)) {
                        videoSearchOptionsToSet.push(
                            { playlists: null, name: `${player.FullName} ${translate('sections.opponent.player.last-25-pa')} ${translate('baseball.abbr-versus')} ${isLHP ? translate('baseball.lhp') : translate('baseball.rhp')}`, batterId, islhp: isLHP, lastXPas: 25, yearId: -1 },
                            { playlists: null, name: `${player.FullName} ${translate('sections.opponent.player.last-25-pa')} ${translate('baseball.abbr-versus')} ${pitcherNameUse}`, batterId, pitcherId: pitcherIdUse, lastXPas: 25, yearId: -3 },
                        );
                    }
                    // Non-Catcher Batter has no action

                    return;
                }
                if (playlistcategory.id === 5 && !IsPitcher && !isCatcherViewingPitcher && isLHB) {
                    // Recent vs NY LHH (batter only)
                    videoSearchOptionsToSet.push(
                        { name: playlistcategory.name, playlists: null, pitcherid: pitcherId, islhp: isLHP, islhb: true, lastXPas: 10, battingTeamId: TeamID || 147, yearId: -1 }, // 10 PAs to try to keep the total number reasonable
                    );

                    lastXGames.forEach((g) => {
                        videoSearchOptionsToSet.push(
                            { name: getLastAppearanceTitle(g), playlists: null, pitcherid: pitcherId, gameid: g.gameid },
                        );
                    });

                    return;
                }
                if (playlistcategory.id === 6 && !IsPitcher && !isCatcherViewingPitcher && !isLHB) {
                    // Recent vs NY RHH (batter only)
                    videoSearchOptionsToSet.push(
                        { name: playlistcategory.name, playlists: null, pitcherid: pitcherId, islhp: isLHP, islhb: false, lastXPas: 10, battingTeamId: TeamID || 147, yearId: -1 },
                    );
                    lastXGames.forEach((g) => {
                        videoSearchOptionsToSet.push(
                            { name: getLastAppearanceTitle(g), playlists: null, pitcherid: pitcherId, gameid: g.gameid },
                        );
                    });
                    return;
                }
                if ((playlistcategory.id === 8 || playlistcategory.id === 9) && !IsPitcher && !isCatcherViewingPitcher) {
                    // Pitcher Pickoffs (batter only)
                    videoSearchOptionsToSet.push(
                        { name: playlistcategory.name, playlists: null, pitcherid: pitcherId, pitcherPicks: playlistcategory.id === 8 ? 1 : 2 }, // send pitcherPicks as 1 for 1B and 2 for 2B
                    );
                }
            }
        });

        const categoryIdsNotAuto = visibleInPortalNotAuto.filter((x) => x.PlayListCategory && x.PlayListCategory.PlayListCategoryID).map((x) => x.CategoryID);
        const categoryIdsNotAutoDist = [...new Set(categoryIdsNotAuto)];
        categoryIdsNotAutoDist.forEach((categoryId) => {
            const matchesThisCatId = visibleInPortalNotAuto.filter((x) => x.PlayListCategory.PlayListCategoryID === categoryId);
            if (matchesThisCatId) {
                videoSearchOptionsToSet.push(
                    { name: matchesThisCatId[0].PlayListCategory.Name, playlists: matchesThisCatId },
                );
            }
        });

        setVideoSearchOptions(videoSearchOptionsToSet || []);
    }

    function vsoClick(simple, data) {
        if (!data) {
            return;
        }

        // If playlist provided, use the user list
        if (simple) {
            // Process custom
            const toDisplay = [];
            let categoryIdToSet = null;
            if (data?.playlists) {
                data.playlists.forEach((list) => {
                    const newDisp = getPlaylist(translate, list.Clips, list.Name);
                    toDisplay.push(newDisp);
                    categoryIdToSet = list.CategoryID;
                });
            } else {
                categoryIdToSet = data.CategoryID;
                toDisplay.push(getPlaylist(translate, data.Clips, data.Name));
            }

            setVideosDisplaySimple(toDisplay[0]);
            setVideosDisplay(null);
            setActiveSearchOption({ name: data.name || data.Name, category: categoryIdToSet });
        } else {
            // Otherwise use the default operation
            setActiveSearchOption(data);
            setVideosDisplaySimple(null);
        }

        if (browser.mobile && !browser.tablet) {
            setShowVideoPlayerModal(true);
            setInitialPageLoad(false);
        }
    }

    useEffect(() => {
        if (videoSearchOptions[0]?.playlists === null) {
            setActiveSearchOption(() => videoSearchOptions[0]);
        } else {
            vsoClick(true, videoSearchOptions[0]);
        }
    }, [player, videoSearchOptions]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (playlistDataRaw) processPlayerPlaylist(playlistDataRaw);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pitcherIfCatcher]);

    const onHideVideoPlayerModal = () => {
        setShowVideoPlayerModal(false);
    };

    const { name: searchTitle, ...videoSearchParams } = !!activeSearchOption && activeSearchOption;
    const { isFetching: isLoading } = useOktaQuery(
        ['videoSearch', videoSearchParams],
        (t) => Video.search(t, videoSearchParams),
        {
            enabled: !!searchTitle,
            onSuccess: ((data) => setVideosDisplay(mapPlaylistFromVideoSearch(data, user.Roles.map((r) => r.RoleName)))),
        },
    );
    const { isFetching: isLoadingLastXAppearances } = useOktaQuery(
        ['lastXAppearances', { pitcherId: opposingPlayerId }],
        (t) => PlayerReport.lastXAppearances(t, { pitcherId: opposingPlayerId }),
        {
            enabled: !IsPitcher && !isCatcherViewingPitcher && !!opposingPlayerId,
            onSuccess: (data) => setLastXGames(data.games),
        },
    );

    return isPlayerLoading || isLoadingPlayerPlaylist || isLoadingLastXAppearances
        ? (<LoadingSpinner />)
        : (
            <div>
                <div ref={videoPlayerModalContainer} />

                {isCatcherViewingPitcher && (
                    <StyledReportHeader style={{ marginBottom: '10px' }}>
                        <div>
                            <b>{translate('common.select-a-pitcher')}:</b>
                            <MatchupPitcherSelector
                                onSelect={setPitcherIfCatcher}
                            />
                        </div>
                    </StyledReportHeader>
                )}
                {searchTitle && (
                    <StyledSearchTitle>
                        {/* <h3 className="flex-shrink-0 mb-1">{player?.FullName}</h3> */ /* Removing this to make room for more buttons */}
                        <div className="ms-3 text-left">
                            {videoSearchOptions.filter((x) => x.playlists === null || (x.playlists != null && x.playlists.length === 1)).map((vso) => (
                                <span key={vso.name}>
                                    {(!browser.mobile || browser.tablet) && (
                                        <>
                                            <Button
                                                style={{ margin: '5px 8px 5px 0px' }}
                                                variant={vso.name === activeSearchOption.name ? 'primary' : 'default'}
                                                size="sm"
                                                onClick={() => vsoClick(vso?.playlists?.length === 1, vso)}
                                                key={vso.name}
                                            >
                                                { vso.name }
                                            </Button>
                                        </>
                                    )}
                                    {browser.mobile && !browser.tablet && (
                                        <>
                                            <Button
                                                style={{ margin: '5px 8px 5px 0px' }}
                                                variant={vso.name === activeSearchOption.name && !initialPageLoad ? 'primary' : 'default'}
                                                size="sm"
                                                onClick={() => vsoClick(vso?.playlists?.length === 1, vso)}
                                                key={vso.name}
                                            >
                                                { vso.name }
                                            </Button>
                                        </>
                                    )}

                                </span>
                            ))}
                            {videoSearchOptions.filter((x) => x.playlists != null && x.playlists.length > 1).map((vsodd) => {
                                const dropdowns = [];
                                vsodd.playlists.forEach((x) => {
                                    dropdowns.push({
                                        key: x.PlayListID,
                                        category: x.CategoryID,
                                        name: x.Name,
                                        value: x,
                                    });
                                });
                                if (dropdowns[0].category === activeSearchOption?.category) {
                                    return (
                                        <Dropdown
                                            title={vsodd.name}
                                            key={vsodd.name}
                                            options={dropdowns}
                                            value={activeSearchOption}
                                            onSelect={(selected) => vsoClick(true, selected?.value)}
                                            variant="primary"
                                            inline
                                        />
                                    );
                                }

                                return (
                                    <Dropdown
                                        title={vsodd.name}
                                        key={vsodd.name}
                                        options={dropdowns}
                                        onSelect={(selected) => vsoClick(true, selected?.value)}
                                        inline
                                    />
                                );
                            })}
                        </div>
                    </StyledSearchTitle>
                )}
                { (isLoading || isLoadingPlayerPlaylist) && <><br /><LoadingSpinner /><br /><br />{translate('sections.opponent.player.loading-videos')}</> }
                { !isLoading && !isLoadingPlayerPlaylist && !videosDisplay && !videosDisplaySimple && <><br /><br />{translate('sections.opponent.player.no-matching-videos')}</> }
                {(!browser.mobile || browser.tablet) && (
                    <>
                        { !isLoading && !isLoadingPlayerPlaylist && videosDisplay && (
                            <>
                                <VideoPlayerAdvanced
                                    playlist={videosDisplay || []}
                                    maxHeight={834}
                                    hideDownload
                                    hideShare
                                    hideFrameGrab
                                    heatMapPitcherIdOverride={IsPitcher && PlayerID}
                                    enableHeatMapFallback
                                />
                            </>
                        )}
                        { !isLoading && !isLoadingPlayerPlaylist && videosDisplaySimple && (
                            <VideoPlayer
                                playlist={videosDisplaySimple}
                                hideDownload
                                hideShare
                                hideFrameGrab
                                heatMapPitcherIdOverride={IsPitcher && PlayerID}
                                enableHeatMapFallback
                            />
                        )}
                    </>
                )}
                {browser.mobile && !browser.tablet && (
                    <>
                        { !isLoading && !isLoadingPlayerPlaylist && videosDisplay && !initialPageLoad && (
                            <>
                                <VideoPlayerAdvanced
                                    playlist={videosDisplay || []}
                                    maxHeight={834}
                                    hideDownload
                                    hideShare
                                    hideFrameGrab
                                    heatMapPitcherIdOverride={IsPitcher && PlayerID}
                                    enableHeatMapFallback
                                />
                            </>
                        )}
                        { !isLoading && !isLoadingPlayerPlaylist && videosDisplay && showVideoPlayerModal && (
                            <VideoPlayerModal
                                playlist={videosDisplay}
                                show={!!videosDisplay}
                                onHide={() => onHideVideoPlayerModal()}
                                container={videoPlayerModalContainer}
                                hideDownload
                                hideShare
                                hideFrameGrab
                                heatMapPitcherIdOverride={IsPitcher && PlayerID}
                                enableHeatMapFallback
                            />
                        )}
                    </>
                )}
            </div>
        );
};

PlayerVideoReviewReport.defaultProps = {
    player: null,
};

export default PlayerVideoReviewReport;
